import { useEffect } from 'react';

/**
 * web-539 fix for scroll - Custom hook for Table of Contents smooth scrolling
 * Prevents TOC links from opening in new tab, instead smooth scrolls to sections
 * Updates browser URL with hash on click and handles initial hash on page load
 * 
 * @param contentSelector - CSS selector for the main content container (e.g., '.blogDetails')
 * @param tocSelector - CSS selector for the TOC container (e.g., '#sanitizedHeroContent')
 * @param dependencies - Array of dependencies to re-run the effect (e.g., [slug, description])
 */
export const useSmoothScroll = (
  contentSelector: string = '.blogDetails',
  tocSelector: string = '#sanitizedHeroContent',
  dependencies: any[] = []
) => {
  useEffect(() => {
    // web-539 fix for scroll - Generate unique IDs for headings (h2) in content
    const generateUniqueIds = (): void => {
      const answerParagraphs = document.querySelectorAll(`${contentSelector} h2`);
      if (answerParagraphs.length === 0) return; // wait for content to load
      
      answerParagraphs.forEach((paragraph, index) => {
        if (paragraph.hasAttribute('id')) return; // don't override an existing id
        const id = `answer_${index}`;
        paragraph.setAttribute('id', id);
      });
    };

    // web-539 fix for scroll - Smooth scroll to target section
    const scrollToTarget = (targetSelector: string, updateUrl: boolean = true): void => {
      const hash = targetSelector.startsWith('#')
        ? targetSelector.slice(1)
        : targetSelector;

      const targetElement = document.getElementById(hash);

      if (targetElement) {
        const topOffset = targetElement.getBoundingClientRect().top;
        const offsetY = window.pageYOffset || document.documentElement.scrollTop;
        const targetTop = topOffset + offsetY - 100; // 100px offset for fixed header
        window.scroll({ top: targetTop, behavior: 'smooth' });
        
        // web-539 fix for scroll - Update browser URL with hash
        if (updateUrl) {
          const newUrl = `${window.location.pathname}${window.location.search}#${hash}`;
          window.history.pushState({}, '', newUrl);
        }
      }
    };

    // web-539 fix for scroll - Global click handler to intercept anchor clicks
    const handleClick = (e: MouseEvent): void | false => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      
      if (!anchor) return;
      
      // Check if this anchor is inside content or TOC
      const inContent = anchor.closest(contentSelector) || anchor.closest(tocSelector);
      if (!inContent) return;
      
      const href = anchor.getAttribute('href') || anchor.href || '';
      
      // Extract hash from href
      let hash = '';
      try {
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.includes('#')) {
          const hashIndex = href.indexOf('#');
          const potentialHash = href.substring(hashIndex);
          const urlWithoutHash = href.substring(0, hashIndex);
          const currentUrl = window.location.href.split('#')[0];
          
          if (!urlWithoutHash || urlWithoutHash === currentUrl || urlWithoutHash === window.location.pathname) {
            hash = potentialHash;
          }
        }
      } catch (err) {
        // Invalid URL, skip
      }
      
      if (hash) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        scrollToTarget(hash, true); // Update URL on click
        return false;
      }
    };

    // web-539 fix for scroll - Attach global click handler with capture
    document.addEventListener('click', handleClick as EventListener, true);

    // web-539 fix for scroll - Normalize anchors (set proper href for hover preview, remove target="_blank")
    const normalizeAnchors = (): void => {
      const anchors = Array.from(
        document.querySelectorAll(`${contentSelector} a[href*='#'], ${tocSelector} a[href*='#']`)
      );
      
      anchors.forEach((anchor) => {
        const href = anchor.getAttribute('href') || '';
        
        // Extract hash from href
        let hash = '';
        try {
          if (href.startsWith('#')) {
            hash = href;
          } else if (href.includes('#')) {
            const hashIndex = href.indexOf('#');
            hash = href.substring(hashIndex);
          }
        } catch (err) {
          // Invalid URL, skip
        }
        
        // web-539 fix for scroll - Set href to relative hash path so browser shows it on hover
        if (hash) {
          anchor.setAttribute('href', hash);
        }
        
        // Remove target and rel attributes
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
      });
    };

    // web-539 fix for scroll - Setup with multiple attempts for lazy-loaded content
    const setupScrolling = (): void => {
      generateUniqueIds();
      normalizeAnchors();
    };

    // web-539 fix for scroll - Run setup immediately on mount (not just with delays)
    // This ensures anchors are normalized before user can click them on first page load
    setupScrolling();

    // web-539 fix for scroll - Handle initial hash on page load
    const handleInitialHash = (): void => {
      if (!window.location.hash) return;
      
      const hash = window.location.hash;
      const hashId = hash.startsWith('#') ? hash.slice(1) : hash;
      
      // Ensure IDs are generated first
      generateUniqueIds();
      
      // Check if target element exists
      const targetElement = document.getElementById(hashId);
      
      if (targetElement) {
        // Element found, scroll to it (don't update URL, it's already there)
        scrollToTarget(hash, false);
      }
    };

    // web-539-v2 fix for scroll - Listen for hash changes (manual URL edits, back/forward navigation)
    const handleHashChange = (): void => {
      if (window.location.hash) {
        // Generate IDs first
        generateUniqueIds();
        
        // Small delay to ensure content is ready
        setTimeout(() => {
          const hash = window.location.hash;
          const hashId = hash.startsWith('#') ? hash.slice(1) : hash;
          const targetElement = document.getElementById(hashId);
          
          if (targetElement) {
            scrollToTarget(hash, false);
          }
        }, 100);
      }
    };

    // Run immediately and at intervals to catch lazy-loaded content
    const timeouts = [100, 500, 1000, 2000, 3000].map((delay) =>
      setTimeout(setupScrolling, delay)
    );

    // web-539-v2 fix for scroll - Attach hashchange listener
    window.addEventListener('hashchange', handleHashChange);

    // web-539 fix for scroll - Try to handle initial hash at intervals (for lazy-loaded content)
    const initialHashTimeouts: NodeJS.Timeout[] = [];
    if (window.location.hash) {
      // web-539-v2 fix for scroll - Try immediately on mount (no delay)
      setupScrolling();
      handleInitialHash();
      
      // web-539-v2 fix for scroll - Then retry at intervals for lazy-loaded content (extended to 7s)
      [200, 500, 1000, 1500, 2000, 3000, 4000, 5000, 6000, 7000].forEach((delay) => {
        initialHashTimeouts.push(
          setTimeout(() => {
            setupScrolling(); // Ensure IDs are generated
            handleInitialHash();
          }, delay)
        );
      });
    }

    // web-539 fix for scroll - Watch for content changes (LazyReveal / dynamic content)
    const observerTargets = [
      document.querySelector(contentSelector),
      document.querySelector(tocSelector)
    ].filter(Boolean) as Element[];

    const observer = observerTargets.length > 0
      ? new MutationObserver(() => {
          setupScrolling();
          // Re-check for hash after content changes (for lazy-loaded content)
          if (window.location.hash) {
            handleInitialHash();
          }
        })
      : null;

    if (observer) {
      observerTargets.forEach((el) =>
        observer.observe(el, { childList: true, subtree: true })
      );
    }

    // web-539 fix for scroll - Cleanup on unmount or dependency change
    return () => {
      timeouts.forEach(clearTimeout);
      initialHashTimeouts.forEach(clearTimeout);
      document.removeEventListener('click', handleClick as EventListener, true);
      window.removeEventListener('hashchange', handleHashChange); // web-539-v2 fix for scroll
      if (observer) {
        observer.disconnect();
      }
    };
  }, dependencies);
};


